import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ticketsApi, techniciansApi, formatApiError } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Plus, 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  Activity,
  AlertTriangle,
  MoreHorizontal,
  User,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from '../../components/ui/dropdown-menu';
import { useAuth } from '../../contexts/AuthContext';

export default function TicketsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: '',
    myTickets: false
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    caller_name: '',
    caller_phone: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    fetchData();
  }, [filters.status, filters.priority, filters.myTickets, page]);

  const fetchData = async () => {
    try {
      const params = { page, page_size: 20 };
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.myTickets) params.my_tickets = true;
      
      const [ticketsRes, techRes] = await Promise.all([
        ticketsApi.getAll(params),
        techniciansApi.getAll()
      ]);
      const data = ticketsRes.data;
      setTickets(data.tickets || []);
      setTotalPages(data.total_pages || 1);
      setTotalTickets(data.total || 0);
      setTechnicians(techRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError('');

    try {
      await ticketsApi.create(createForm);
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        description: '',
        priority: 'medium',
        category: 'general',
        caller_name: '',
        caller_phone: ''
      });
      fetchData();
    } catch (error) {
      setCreateError(formatApiError(error));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAssignTicket = async (techId) => {
    if (!selectedTicket) return;
    
    try {
      await ticketsApi.update(selectedTicket.id, { 
        assigned_to: techId,
        status: 'in_progress'
      });
      setShowAssignModal(false);
      setSelectedTicket(null);
      fetchData();
    } catch (error) {
      console.error('Assign error:', error);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await ticketsApi.update(ticketId, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-[#DC2626] border-[#DC2626]',
      high: 'bg-amber-100 text-[#D97706] border-[#D97706]',
      medium: 'bg-blue-100 text-[#002FA7] border-[#002FA7]',
      low: 'bg-green-100 text-[#16A34A] border-[#16A34A]'
    };
    return styles[priority] || styles.medium;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Clock className="w-4 h-4 text-[#002FA7]" />;
      case 'in_progress': return <Activity className="w-4 h-4 text-[#D97706]" />;
      case 'resolved': return <CheckCircle className="w-4 h-4 text-[#16A34A]" />;
      default: return <Clock className="w-4 h-4 text-[#71717A]" />;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return ticket.title.toLowerCase().includes(search) || 
             ticket.description.toLowerCase().includes(search) ||
             ticket.id?.toLowerCase().includes(search);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Gestione Ticket
          </h1>
          <p className="text-[#71717A] mt-1">Gestisci e monitora tutti i ticket di assistenza</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
          data-testid="create-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Ticket
        </Button>
      </div>

      {/* Filters */}
      <Card className="border border-[#E4E4E7] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                placeholder="Cerca ticket..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9 border-[#E4E4E7]"
                data-testid="search-tickets-input"
              />
            </div>
            <Select
              value={filters.myTickets ? 'my_tickets' : (filters.status || 'all')}
              onValueChange={(value) => { 
                if (value === 'my_tickets') {
                  setFilters({ ...filters, status: '', myTickets: true }); 
                } else {
                  setFilters({ ...filters, status: value === 'all' ? '' : value, myTickets: false }); 
                }
                setPage(1); 
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px] border-[#E4E4E7]" data-testid="filter-status-select">
                <Filter className="w-4 h-4 mr-2 text-[#71717A]" />
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="open">Aperto</SelectItem>
                <SelectItem value="in_progress">In corso</SelectItem>
                <SelectItem value="resolved">Risolto</SelectItem>
                <SelectItem value="closed">Chiuso</SelectItem>
                <SelectItem value="my_tickets">I miei ticket</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.priority}
              onValueChange={(value) => { setFilters({ ...filters, priority: value === 'all' ? '' : value }); setPage(1); }}
            >
              <SelectTrigger className="w-full sm:w-[180px] border-[#E4E4E7]" data-testid="filter-priority-select">
                <AlertTriangle className="w-4 h-4 mr-2 text-[#71717A]" />
                <SelectValue placeholder="Priorità" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le priorità</SelectItem>
                <SelectItem value="urgent">Urgente</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="medium">Media</SelectItem>
                <SelectItem value="low">Bassa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ── Mobile: card list (hidden on md+) ── */}
      <div className="md:hidden space-y-3" data-testid="tickets-mobile-list">
        {filteredTickets.length === 0 ? (
          <div className="text-center py-12 text-[#71717A]">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nessun ticket trovato</p>
          </div>
        ) : (
          filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              className="bg-white rounded-lg border border-[#E4E4E7] p-4 cursor-pointer active:bg-[#F4F4F5] transition-colors"
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              data-testid={`ticket-card-${ticket.id}`}
            >
              {/* Header row */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="font-medium text-[#0F172A] text-sm leading-tight flex-1">{ticket.title}</p>
                <Badge className={`${getPriorityBadge(ticket.priority)} border text-xs shrink-0`}>
                  {ticket.priority}
                </Badge>
              </div>
              {/* Meta row */}
              <div className="flex items-center gap-3 text-xs text-[#71717A]">
                <div className="flex items-center gap-1">
                  {getStatusIcon(ticket.status)}
                  <span className="capitalize">{ticket.status.replace('_', ' ')}</span>
                </div>
                <span>·</span>
                <span className="capitalize">{ticket.category}</span>
                <span>·</span>
                <span className="font-mono">#{ticket.id?.slice(-6)}</span>
              </div>
              {/* Footer row */}
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#F4F4F5]">
                <span className="text-xs text-[#71717A]">
                  {ticket.assigned_technician_name ? (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {ticket.assigned_technician_name}
                    </span>
                  ) : (
                    <button
                      className="text-[#002FA7] font-medium flex items-center gap-1"
                      onClick={(e) => { e.stopPropagation(); setSelectedTicket(ticket); setShowAssignModal(true); }}
                    >
                      <User className="w-3 h-3" /> Assegna
                    </button>
                  )}
                </span>
                <span className="text-xs text-[#71717A]">
                  {new Date(ticket.created_at).toLocaleDateString('it-IT')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Desktop: table (hidden on mobile) ── */}
      <Card className="hidden md:block border border-[#E4E4E7] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                <TableHead className="font-medium">Ticket</TableHead>
                <TableHead className="font-medium">Stato</TableHead>
                <TableHead className="font-medium">Priorità</TableHead>
                <TableHead className="font-medium">Categoria</TableHead>
                <TableHead className="font-medium">Assegnato a</TableHead>
                <TableHead className="font-medium">Data</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <div className="text-[#71717A]">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nessun ticket trovato</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket) => (
                  <TableRow 
                    key={ticket.id} 
                    className="hover:bg-[#F4F4F5] cursor-pointer border-b border-[#E4E4E7]"
                    data-testid={`ticket-table-row-${ticket.id}`}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-[#0F172A]">{ticket.title}</p>
                        <p className="text-xs text-[#71717A] font-mono">#{ticket.id?.slice(-8)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(ticket.status)}
                        <span className="capitalize text-sm">{ticket.status.replace('_', ' ')}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getPriorityBadge(ticket.priority)} border text-xs`}>
                        {ticket.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{ticket.category}</TableCell>
                    <TableCell>
                      {ticket.assigned_technician_name ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#E4E4E7] flex items-center justify-center text-xs font-medium">
                            {ticket.assigned_technician_name.charAt(0)}
                          </div>
                          <span className="text-sm">{ticket.assigned_technician_name}</span>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTicket(ticket);
                            setShowAssignModal(true);
                          }}
                          className="text-[#002FA7] hover:text-[#002FA7]/80 h-auto p-1"
                          data-testid={`assign-btn-${ticket.id}`}
                        >
                          <User className="w-4 h-4 mr-1" />
                          Assegna
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#71717A]">
                      {new Date(ticket.created_at).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${ticket.id}`); }}>
                            Visualizza
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(ticket.id, 'in_progress'); }}>
                            Imposta In Corso
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(ticket.id, 'resolved'); }}>
                            Segna Risolto
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="pagination">
          <p className="text-sm text-[#71717A]">
            Pagina {page} di {totalPages} • {totalTickets} ticket totali
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="border-[#E4E4E7]"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Precedente
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'default' : 'outline'}
                  size="sm"
                  className={pageNum === page ? 'bg-[#002FA7] text-white' : 'border-[#E4E4E7]'}
                  onClick={() => setPage(pageNum)}
                  data-testid={`pagination-page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="border-[#E4E4E7]"
              data-testid="pagination-next"
            >
              Successivo
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Nuovo Ticket
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTicket} className="space-y-4 mt-2">
            {createError && (
              <div className="p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md">
                {createError}
              </div>
            )}
            
            <div className="space-y-1.5">
              <Label htmlFor="title" className="text-sm font-medium text-[#0F172A]">Titolo *</Label>
              <Input
                id="title"
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                required
                placeholder="Descrivi brevemente il problema"
                data-testid="ticket-title-input"
                className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-sm font-medium text-[#0F172A]">Descrizione *</Label>
              <Textarea
                id="description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                required
                placeholder="Fornisci dettagli sul problema..."
                rows={4}
                data-testid="ticket-description-input"
                className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="priority" className="text-sm font-medium text-[#0F172A]">Priorità</Label>
                <Select
                  value={createForm.priority}
                  onValueChange={(value) => setCreateForm({ ...createForm, priority: value })}
                >
                  <SelectTrigger data-testid="ticket-priority-select" className="h-10 border-[#E4E4E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Bassa</SelectItem>
                    <SelectItem value="medium">Media</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="category" className="text-sm font-medium text-[#0F172A]">Categoria</Label>
                <Select
                  value={createForm.category}
                  onValueChange={(value) => setCreateForm({ ...createForm, category: value })}
                >
                  <SelectTrigger data-testid="ticket-category-select" className="h-10 border-[#E4E4E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Generale</SelectItem>
                    <SelectItem value="hardware">Hardware</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="network">Rete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="caller_name" className="text-sm font-medium text-[#0F172A]">Nome Richiedente</Label>
                <Input
                  id="caller_name"
                  value={createForm.caller_name}
                  onChange={(e) => setCreateForm({ ...createForm, caller_name: e.target.value })}
                  placeholder="Mario Rossi"
                  data-testid="ticket-caller-name-input"
                  className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="caller_phone" className="text-sm font-medium text-[#0F172A]">Telefono</Label>
                <Input
                  id="caller_phone"
                  value={createForm.caller_phone}
                  onChange={(e) => setCreateForm({ ...createForm, caller_phone: e.target.value })}
                  placeholder="+39 123 456 7890"
                  data-testid="ticket-caller-phone-input"
                  className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>
            </div>

            <DialogFooter className="mt-6 pt-4 border-t border-[#E4E4E7]">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="border-[#E4E4E7]">
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
                disabled={createLoading}
                data-testid="submit-ticket-btn"
              >
                {createLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  'Crea Ticket'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Ticket Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="sm:max-w-[400px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Assegna Ticket
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#71717A]">
              Seleziona un tecnico per assegnare il ticket "{selectedTicket?.title}"
            </p>
            <div className="space-y-2">
              {technicians.filter(t => t.status === 'available').length === 0 ? (
                <p className="text-center text-[#71717A] py-4">Nessun tecnico disponibile</p>
              ) : (
                technicians
                  .filter(t => t.status === 'available')
                  .map((tech) => (
                    <Button
                      key={tech.id}
                      variant="outline"
                      className="w-full justify-start h-auto p-3 border-[#E4E4E7] hover:border-[#002FA7] hover:bg-blue-50"
                      onClick={() => handleAssignTicket(tech.id)}
                      data-testid={`assign-to-${tech.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#E4E4E7] flex items-center justify-center font-medium text-[#0F172A]">
                          {tech.name.charAt(0)}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-[#0F172A]">{tech.name}</p>
                          <p className="text-xs text-[#71717A]">{tech.specialty} - {tech.tickets_assigned} ticket attivi</p>
                        </div>
                      </div>
                    </Button>
                  ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
